import { Modal, Table, Badge, Alert, Button, Container, Row, Col } from "react-bootstrap";
import { useAppSelector } from "../../../../../redux/hooks";
import { ReducerEnum, setClientPortalModalType } from "../../../../../redux/reducer";
import { useContext, useEffect, useState } from "react";
import StateContext from "../../../context/State";
import { ClientPortalModalType, ClientPortalPage } from "../../../types";
import ConsentFormStep from '../../Initiate/NewClientPortal/ConsentFormStep';
import { useConsentFormConfiguration } from '../../../hooks/useConsentFormConfiguration';
import { useClientInformation } from '../../../hooks/useClientInformation';
import { useConsentFormInformationQuery } from '../../../hooks/useConsentFormInformationQuery';
import { validateConsentForm } from '../../../utils/documentValidation';
import { format } from 'date-fns';
import PortalService from '../../../services/portal.service';
import { buildConsentFormPdfBlobUrl } from './ConsentFormPdfTemplate';


export type ClientConsentFormTaskModalProps = {
    dispatch: (arg0: {
        type: ReducerEnum;
        payload: any;
        key?: string;
        extraPayload?: Record<string, string>;
    }) => void;
}

const ClientConsentFormTaskModal = (props: ClientConsentFormTaskModalProps) => {

    const { state, setState } = useContext(StateContext);
    const portalService = new PortalService(state);

    const show: boolean = useAppSelector((state) => state.ClientPortalState?.ModalType === ClientPortalModalType.INFORMATION_SHARING_CONSENT);

    const { data: clients = [] } = useClientInformation(show);
    const {
        isConsentFormRequired,
        consentFormConfig,
        refetch: refreshConsentFormData
    } = useConsentFormInformationQuery(show);

    const isPdfConsent =
        Boolean(consentFormConfig?.UploadConsent) ||
        Boolean(consentFormConfig?.DefaultConsentRequired);
    const isTextConsent = Boolean(consentFormConfig?.TextBoxConsentRequired);
    const textConsentContent: string = consentFormConfig?.TextBoxConsentContent ?? '';
    const hasRenderableContent = isPdfConsent || (isTextConsent && textConsentContent.trim().length > 0);
    const anyAcknowledged = clients.some(client => Boolean(client.ConsentAcknowledged));
    const actionsDisabled = !anyAcknowledged;

    const signaturesKey = clients
        .filter(c => Boolean(c.ConsentAcknowledged) && c.ConsentAcknowledgedDateTime)
        .map(c => `${c.Name}|${c.ConsentAcknowledgedDateTime}`)
        .join(';');

    const [blobUrl, setBlobUrl] = useState<string | null>(null);

    const {
        consentFormState,
        setConsentFormState,
        setUploadedConsentFile,
        uploadedConsentFile,
        isSubmitting,
        handleSave,
        documentSavingPreference
    } = useConsentFormConfiguration({
        portalId: Number(state.portalId),
        appId: state.appId,
        setState: setState,
        onSuccess: refreshConsentFormData,
        onError: (error) => alert(error)
    });

    // PDF mode: fetch the lawyer-uploaded consent form PDF from backend.
    useEffect(() => {
        if (!show || !isPdfConsent || !anyAcknowledged) {
            return;
        }

        let cancelled = false;
        let createdUrl: string | null = null;

        portalService.getConsentFormBlobUrl(state.portalId)
            .then(url => {
                if (cancelled) {
                    window.URL.revokeObjectURL(url);
                    return;
                }
                createdUrl = url;
                setBlobUrl(url);
            })
            .catch(e => console.error('Unable to load consent form:', e));

        return () => {
            cancelled = true;
            if (createdUrl) {
                window.URL.revokeObjectURL(createdUrl);
            }
            setBlobUrl(null);
        };
    }, [show, isPdfConsent, anyAcknowledged, state.portalId]);

    // Text mode: generate PDF locally from text + signatures.
    useEffect(() => {
        if (!show || !isTextConsent || !anyAcknowledged || textConsentContent.trim().length === 0) {
            return;
        }

        const signatures = clients
            .filter(c => Boolean(c.ConsentAcknowledged) && c.ConsentAcknowledgedDateTime)
            .map(c => ({
                name: c.Name,
                signedAt: c.ConsentAcknowledgedDateTime
            }));
        const url = buildConsentFormPdfBlobUrl(textConsentContent, signatures);
        setBlobUrl(url);

        return () => {
            window.URL.revokeObjectURL(url);
            setBlobUrl(null);
        };
    }, [show, isTextConsent, textConsentContent, anyAcknowledged, signaturesKey]);

    const handlePreview = () => {
        if (!blobUrl) return;
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Consent Form Preview</title>
                <style>
                    html, body { margin: 0; padding: 0; height: 100%; overflow: hidden; }
                    iframe { width: 100%; height: 100%; border: 0; }
                </style>
            </head>
            <body>
                <iframe src="${blobUrl}#toolbar=0"></iframe>
            </body>
            </html>
        `;

        const consentPreviewWindow = window.open(' ', '_blank');
        if (consentPreviewWindow) {
            consentPreviewWindow.document.write(htmlContent);
            consentPreviewWindow.document.close();
        }
        else {
            alert('Allow pop-ups to preview the consent form in a new window.');
        }
    };

    const handleSaveAndClose = async () => {
        // Validate consent form before saving
        const validation = validateConsentForm(consentFormState, uploadedConsentFile);
        if (!validation.isValid) {
            alert(validation.error);
            return; // Don't proceed if validation fails
        }
        
        props.dispatch(setClientPortalModalType(ClientPortalModalType.NONE));
        
        setState({ ...state, page: ClientPortalPage.LOADING });
        
        try {
            await handleSave();
        } finally {
            setState({ ...state, page: ClientPortalPage.DASHBOARD, progressMessage: undefined });
        }
    };
    
    return (
        <>
            <Modal
                show={show}
                onHide={() => { props.dispatch(setClientPortalModalType(ClientPortalModalType.NONE)) }}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                className="consent-form-modal"
            >
                <Modal.Header closeButton>
                    <Container className="gx-0">
                        <Row className="gx-0">
                            <Col>
                                <Modal.Title id="contained-modal-title-vcenter">Information Sharing Consent Form Status</Modal.Title>
                            </Col>
                            {hasRenderableContent && (
                                <Col className="align-right">
                                    <Button
                                        variant="outline-primary"
                                        title="Preview Consent Form"
                                        onClick={handlePreview}
                                        disabled={actionsDisabled || !blobUrl}
                                        style={{ marginRight: '15px' }}
                                    >
                                        <i className="fa-regular fa-eye"></i> Preview
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    </Container>
                </Modal.Header>

                <Modal.Body>
                    {!isConsentFormRequired ? (
                        <>
                            <Alert>Consent Form not Required</Alert>
                            <ConsentFormStep
                                consentFormState={consentFormState}
                                setConsentFormState={setConsentFormState}
                                setUploadedConsentFile={setUploadedConsentFile}
                                documentSavingPreference={documentSavingPreference}
                            />
                        </>
                    ) : (
                        <div>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Status</th>
                                        <th>Date Submitted</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clients ?
                                        <>
                                            {clients.map((client, index) => (
                                                <tr key={index}>
                                                    <td>{client.Name}</td>
                                                    <td>{client.ConsentAcknowledged ? <Badge bg="success" text="dark" pill>Submitted</Badge> : <Badge bg="secondary" text="dark" pill>Not Submitted</Badge>} </td>
                                                    <td>
                                                        {client.ConsentAcknowledged && client.ConsentAcknowledgedDateTime ? (
                                                            <Badge
                                                                pill
                                                                text="dark"
                                                                bg="info"
                                                            >
                                                                {format(new Date(client.ConsentAcknowledgedDateTime), "MMMM do, yyyy 'at' h:mm a")}
                                                            </Badge>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </>
                                        :
                                        <></>
                                    }
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                
                { !isConsentFormRequired && <Modal.Footer>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveAndClose}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : 'Save & Notify Clients'}
                    </Button>
                </Modal.Footer>
                }
            </Modal>
        </>
    );
}

export default ClientConsentFormTaskModal;
